import * as React from 'react';

import {
  LanguagePicker,
  TeacherPreviewMenuButton,
  renderSaveAndExitButton,
  showTranslations,
  hasTeacherPreview
} from '../../index';

const quillLogoSrc = `${window.postbuildEnv.CDN_URL}/images/logos/quill-logo-white-2022.svg`;

export type ToolName = 'connect' | 'diagnostic' | 'grammar' | 'lessons' | 'proofreader';

export interface ActivityNavbarProps {
  checkTeacherIdentity?: boolean; // If true, checks isTeacher/hasTeacherPreview(); if false, always shows
  helpLink?: string; // If provided, renders a Help link/button on the right instead of Save and Exit
  isELLDiagnostic?: boolean;
  isOnMobile?: boolean;
  isTeacher?: boolean;
  language?: string;
  languageOptions?: { value: string; label: string }[];
  layoutVariant?: 'standard' | 'student';
  onSaveAndExit?: () => void;  // Custom handler - if provided, renders button instead of anchor
  onTogglePreview?: () => void;
  previewShowing?: boolean;
  showLanguagePicker?: boolean;
  showTeacherPreview?: boolean;
  title?: string; // If provided, renders a vertical divider + title text after the tool name
  toolName: ToolName;
  translate?: (key: string) => string;
  updateLanguage?: (language: string) => void;
  useShowTranslationsCheck?: boolean; // If true, uses showTranslations() helper; if false, just checks language truthy
}

export const ActivityNavbar: React.FC<ActivityNavbarProps> = ({
  checkTeacherIdentity = true,
  helpLink,
  isELLDiagnostic = false,
  isOnMobile = false,
  isTeacher = false,
  language,
  languageOptions,
  layoutVariant = 'standard',
  onSaveAndExit,
  onTogglePreview,
  previewShowing = false,
  showLanguagePicker = false,
  showTeacherPreview = false,
  title,
  toolName,
  translate,
  updateLanguage,
  useShowTranslationsCheck = false,
}) => {
  const shouldShowTeacherPreview = (): boolean => {
    if (!showTeacherPreview || !onTogglePreview || previewShowing || isOnMobile) return false;
    if (!checkTeacherIdentity) return true;
    return isTeacher || hasTeacherPreview();
  };

  const shouldShowLanguagePicker = (): boolean => {
    if (!showLanguagePicker) return false;
    if (useShowTranslationsCheck) return showTranslations(language, languageOptions);
    return !!language;
  };

  const renderHelpLink = () => (
    <a className="navbar-help-button focus-on-dark" href={helpLink} rel="noopener noreferrer" target="_blank">
      Help
    </a>
  );

  const renderSaveAndExit = () => {
    if (onSaveAndExit) {
      return (
        <button className="quill-button medium outlined green focus-on-dark" onClick={onSaveAndExit} type="button">
          Save and Exit
        </button>
      );
    }
    return renderSaveAndExitButton({ isELLDiagnostic, language, languageOptions, translate });
  };

  if (layoutVariant === 'student') {
    return (
      <header className="nav student-nav" data-tool={toolName}>
        <div className="container">
          <div className="student-nav-section logo-and-tool-name">
            <a aria-label="Quill" className="student-nav-item focus-on-dark" href={window.postbuildEnv.DEFAULT_URL} tabIndex={0}>
              <img alt="Quill.org logo" src={quillLogoSrc} />
            </a>
            <span className="tool-name">{toolName}</span>
          </div>
          <div className="header-buttons-container">
            {shouldShowLanguagePicker() && <LanguagePicker language={language} languageOptions={languageOptions} updateLanguage={updateLanguage} />}
            {renderSaveAndExit()}
          </div>
        </div>
      </header>
    );
  }

  const headerClassName = `header${previewShowing ? ' preview-showing' : ''}`;

  return (
    <div className={headerClassName} data-tool={toolName}>
      <div className="activity-navbar-content">
        {shouldShowTeacherPreview() && <TeacherPreviewMenuButton handleTogglePreview={onTogglePreview} />}
        <div className="logo-and-tool-name">
          <a className="focus-on-dark" href={window.postbuildEnv.DEFAULT_URL}>
            <img alt="Quill logo" src={quillLogoSrc} />
          </a>
          <span className="tool-name">{toolName}</span>
        </div>
        {title && <span aria-hidden="true" className="navbar-vertical-divider" />}
        {title && <span className="navbar-title">{title}</span>}
        <div className="header-buttons-container">
          {shouldShowLanguagePicker() && <LanguagePicker language={language} languageOptions={languageOptions} updateLanguage={updateLanguage} />}
          {helpLink ? renderHelpLink() : renderSaveAndExit()}
        </div>
      </div>
    </div>
  );
};

export default ActivityNavbar;
